import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { ChevronLeft, ChevronRight, X, Loader2, Check } from "lucide-react";
import { localIsoDate, parseLocalDate } from "@/lib/utils";

/**
 * ShootCalendar — the monthly grid used both by the top-level /calendar page
 * and by the /team → "Shoot Calendar" tab. Renders bookings + post-production
 * events; clicking any event opens the AttendanceModal for that day so the
 * viewer can log what each staff member worked on.
 *
 * The component is header-less by default (`showHeader={false}`) so it drops
 * cleanly into a tab. Pass `showHeader` to render the "Calendar" page title.
 */
export default function ShootCalendar({ showHeader = false }) {
  const [bookings, setBookings] = useState([]);
  const [pp, setPp] = useState([]);
  const [attendanceFor, setAttendanceFor] = useState(null); // { date, label }
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    api.get("/bookings")
      .then((r) => setBookings(r.data))
      .catch((e) => logger.debug("bookings load failed:", e?.message));
    api.get("/postproduction")
      .then((r) => setPp(r.data))
      .catch((e) => logger.debug("pp load failed:", e?.message));
  }, []);

  const events = useMemo(() => {
    const map = new Map();
    const add = (dateStr, item) => {
      // Use LOCAL date for keying — never `.toISOString()` because that shifts
      // ±1 day across non-UTC timezones (the IST bug).
      const key = localIsoDate(parseLocalDate(dateStr));
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    };
    bookings.forEach((b) => {
      ["day1", "day2", "day3", "day4", "day5"].forEach((k, i) => {
        if (b[k]) add(b[k], { kind: "booking", label: `${b.client_name || b.couple_name || "Booking"}${b.event ? " · " + b.event : ""}`, dayIdx: i + 1, status: b.status });
      });
    });
    pp.forEach((p) => add(p.event_date, { kind: "pp", label: `${p.couple_name} · ${p.event}`, status: p.status }));
    return map;
  }, [bookings, pp]);

  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, m, d));

  const monthLabel = month.toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4 fade-in" data-testid="shoot-calendar">
      <div className="flex items-end justify-between gap-4">
        {showHeader ? (
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight">Calendar</h1>
            <p className="text-stone-500 text-sm mt-1">Events at a glance</p>
          </div>
        ) : (
          <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
            Upcoming shoots &amp; post-production
          </div>
        )}
        <div className="flex items-center gap-2">
          <button className="btn btn-outline" onClick={() => setMonth(new Date(year, m - 1, 1))} data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
          </button>
          <div className="font-heading font-semibold text-lg min-w-[180px] text-center">{monthLabel}</div>
          <button className="btn btn-outline" onClick={() => setMonth(new Date(year, m + 1, 1))} data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider text-stone-500 font-semibold bg-stone-50 border-b border-stone-200">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-3 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`empty-${idx}`} className="border-b border-r border-stone-100 min-h-[110px] bg-stone-50/40" />;
            const key = localIsoDate(cell);
            const evs = events.get(key) || [];
            const isToday = key === localIsoDate(new Date());
            return (
              <div key={key} className="border-b border-r border-stone-100 min-h-[110px] p-2 hover:bg-stone-50" data-testid={`calendar-cell-${key}`}>
                <div className={`text-xs font-mono ${isToday ? "inline-block bg-rose-700 text-white rounded-full w-6 h-6 leading-6 text-center" : "text-stone-500"}`}>
                  {cell.getDate()}
                </div>
                <div className="mt-1 space-y-1">
                  {evs.slice(0, 3).map((e, i) => (
                    <button
                      type="button"
                      key={`${key}-${e.kind}-${e.label}-${i}`}
                      onClick={() => setAttendanceFor({ date: key, label: e.label })}
                      className={`w-full text-left text-[11px] truncate rounded px-1.5 py-0.5 hover:brightness-95 transition ${e.kind === "pp" ? "bg-amber-50 text-amber-900 border border-amber-100" : "bg-rose-50 text-rose-900 border border-rose-100"}`}
                      title={`${e.label} — click to mark attendance`}
                      data-testid={`calendar-event-${key}`}
                    >
                      {e.kind === "booking" && e.dayIdx ? `D${e.dayIdx} · ` : ""}{e.label}
                    </button>
                  ))}
                  {evs.length > 3 && <div className="text-[10px] text-stone-500">+{evs.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-4 text-xs text-stone-500">
        <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm bg-rose-300" /> Bookings</span>
        <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm bg-amber-300" /> Post-production</span>
        <span className="ml-auto text-stone-400">Tip: click any event to log daily ops for the team.</span>
      </div>

      {attendanceFor && (
        <AttendanceModal
          date={attendanceFor.date}
          label={attendanceFor.label}
          onClose={() => setAttendanceFor(null)}
        />
      )}
    </div>
  );
}

export function AttendanceModal({ date, label, onClose }) {
  const [staff, setStaff] = useState([]);
  const [cells, setCells] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/team/staff"),
      api.get(`/team/matrix?start=${date}&end=${date}`),
    ]).then(([{ data: s }, { data: g }]) => {
      setStaff(s);
      const m = {};
      (g.cells || []).forEach((c) => { m[c.staff_id] = c.text; });
      setCells(m);
    }).finally(() => setLoading(false));
  }, [date]);

  const setOne = (sid, val) => setCells((prev) => ({ ...prev, [sid]: val }));
  const bulk = (val) => { const m = {}; staff.forEach((s) => { m[s.id] = val; }); setCells(m); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = staff.map((s) => ({ staff_id: s.id, date, text: cells[s.id] || "", completed: false }));
      await api.post("/team/matrix/bulk", { cells: payload });
      setDone(true);
      setTimeout(onClose, 900);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4 fade-in" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="attendance-modal">
        <div className="flex items-center justify-between px-5 h-14 border-b border-stone-200">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Attendance · {date}</div>
            <div className="font-heading font-semibold truncate max-w-md">{label}</div>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-stone-500"><Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading team…</div>
        ) : done ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3">
              <Check className="w-6 h-6 text-emerald-700" strokeWidth={2} />
            </div>
            <div className="font-heading font-semibold">Attendance saved</div>
          </div>
        ) : (
          <>
            <div className="px-5 pt-3 flex flex-wrap gap-1 text-xs">
              <span className="text-stone-500 mr-1">Batch:</span>
              <button className="text-xs px-2.5 py-1 rounded-full border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100" onClick={() => bulk("SHOOT")} data-testid="attendance-all-present">All Present (SHOOT)</button>
              <button className="text-xs px-2.5 py-1 rounded-full border border-amber-200 text-amber-800 bg-amber-50 hover:bg-amber-100" onClick={() => bulk("HOLIDAY")} data-testid="attendance-all-holiday">All HOLIDAY</button>
              <button className="text-xs px-2.5 py-1 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50" onClick={() => bulk("")}>Clear</button>
            </div>
            <div className="p-5 space-y-2 max-h-[55vh] overflow-y-auto">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center gap-3" data-testid={`attendance-row-${s.id}`}>
                  <div className="w-24 shrink-0 text-sm font-semibold text-stone-800 truncate">{s.name}</div>
                  <input
                    className="input py-1 flex-1 text-sm"
                    value={cells[s.id] || ""}
                    onChange={(e) => setOne(s.id, e.target.value)}
                    placeholder="What did they work on? Type HOLIDAY for a day off"
                    data-testid={`attendance-input-${s.id}`}
                  />
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2">
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="attendance-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" strokeWidth={2} />} Save attendance
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
